import api from '../api'

/**
 * AuthApi class provides various methods for user authentication and profile management.
 * It includes functionalities like updating user details, recovering accounts, verifying
 * registration, changing network, uploading avatars and logos, updating notification
 * settings, changing passwords, and managing menu visibility.
 */
class AuthApi {
    /**
     * Authenticates a user by sending their email and password to the server.
     *
     * @param {{email: string, password: string, termsAccepted: boolean, termsVersion?: string}} param0 - User credentials.
     * @returns {Promise<Object>} The response data from the authentication request.
     */
    async login({ email, password, termsAccepted, termsVersion }) {
        const payload = { email, password, termsAccepted, termsVersion }

        return api
            .sentRequest(
                '/api/v2/auth/login',
                payload,
                false,
                null,
                null,
                'POST',
                'application/json'
            )
            .then((res) => res.data)
            .catch((err) => err.response?.data || api.getCatchResponse(err))
    }

    async logout() {
        return api
            .sentRequest(
                '/api/v2/auth/logout',
                null,
                false,
                null,
                null,
                'POST',
                'application/json'
            )
            .then((res) => res.data)
            .catch((err) => err.response?.data || api.getCatchResponse(err))
    }

    /**
     * Registers a new user by sending their details to the registration API endpoint.
     *
     * @param {Object} params - The registration parameters.
     * @param {string} params.first_name - The first name of the user.
     * @param {string} params.last_name - The last name of the user.
     * @param {string} params.phone_number - The phone number of the user.
     * @param {string} params.email - The email address of the user.
     * @param {string} params.password - The password for the user's account.
     * @param {string} params.company_name - The name of the user's company.
     * @param {string} params.company_url - The URL of the user's company.
     * @param {string} params.token - An authentication token for the registration.
     * @param {string} params.reg_cookie - A registration cookie.
     * @param {string} params.invite_ref_id - An invitation reference ID.
     * @param {string} params.country_code - The country code of the user.
     * @param {string} params.version_tag - The terms of service version tag.
     * @returns {Promise<Object>} The response data from the registration API.
     */
    async register({
        first_name,
        last_name,
        phone_number,
        email,
        password,
        company_name,
        company_url,
        token,
        reg_cookie,
        invite_ref_id,
        country_code,
        version_tag
    }) {
        const payload = {
            first_name,
            last_name,
            phone_number,
            email,
            password,
            company_name,
            company_url,
            token,
            reg_cookie,
            invite_ref_id,
            country_code,
            version_tag
        }

        return api
            .sentRequest('/api/auth/register', payload)
            .then((res) => {
                return res.data
            })
            .catch((err) => {
                console.error('[Auth Api]: ', err)
                return api.getCatchResponse(err)
            })
    }

    /**
     * Updates user information with the provided details.
     *
     * @param {Object} param0 - Object containing user details to update.
     * @param {string} param0.first_name - User's first name.
     * @param {string} param0.last_name - User's last name.
     * @param {string} param0.phone_number - User's phone number.
     * @param {string} param0.company_name - User's company name.
     * @param {string} param0.company_url - User's company URL.
     * @param {string} param0.country_code - User's country code.
     * @returns {Promise<Object>} Updated user data from the API response.
     */
    update({
        first_name,
        last_name,
        phone_number,
        company_name,
        company_url,
        country_code
    }) {
        const payload = {
            first_name,
            last_name,
            phone_number,
            company_name,
            company_url,
            country_code
        }

        return api
            .sentRequest('/api/auth/update', payload)
            .then((res) => {
                return res.data
            })
            .catch((err) => {
                console.error('[Auth Api]: ', err)
                return api.getCatchResponse(err)
            })
    }

    /**
     * Initiates a password recovery process by sending the user's email to the recovery endpoint.
     *
     * @param {Object} param0 - The input parameter object.
     * @param {string} param0.usr_email - The email address of the user requesting password recovery.
     * @returns {Promise<Object>} The response data from the recovery API.
     */
    recover({ usr_email }) {
        const payload = { usr_email }

        return api
            .sentRequest('/api/auth/recovery', payload)
            .then((res) => {
                return res.data
            })
            .catch((err) => {
                console.error('[Auth Api]: ', err)
                return api.getCatchResponse(err)
            })
    }

    /**
     * Verifies the recovery process for a user's account by sending a request to the recovery verification API endpoint.
     *
     * @param {Object} params - The parameters for recovery verification.
     * @param {string} params.usr_email - The email of the user attempting recovery.
     * @param {string} params.recovery_token - The token associated with the recovery process.
     * @param {string} params.recovery_code - The code sent to the user for verification.
     * @param {string} params.password - The new password to be set for the user's account.
     * @returns {Promise<Object>} The response data from the server.
     */
    recoverVerify({ usr_email, recovery_token, recovery_code, password }) {
        const payload = { usr_email, recovery_token, recovery_code, password }

        return api
            .sentRequest('/api/auth/recovery-verify', payload)
            .then((res) => {
                return res.data
            })
            .catch((err) => {
                console.error('[Auth Api]: ', err)
                return api.getCatchResponse(err)
            })
    }

    /**
     * Verifies the user registration using the provided email and verification code.
     *
     * @param {{usr_email: string, verification_code: string}} param0 - The verification payload.
     * @returns {Promise<Object>} The server response data if the request is successful, or an error response if it fails.
     */
    registerVerify({ usr_email, verification_code }) {
        const payload = { usr_email, verification_code }

        return api
            .sentRequest('/api/auth/register-verify', payload)
            .then((res) => {
                return res.data
            })
            .catch((err) => {
                console.error('[Auth Api]: ', err)
                return api.getCatchResponse(err)
            })
    }

    /**
     * Retrieves the currently authenticated user's data by sending a request to the API.
     *
     * @returns {Promise<Object>} The user's data if the request is successful, or a catch response if an error occurs.
     */
    me() {
        const payload = {}

        return api
            .sentRequest('/api/auth/me', payload)
            .then((res) => {
                return res.data
            })
            .catch((err) => {
                console.error('[Auth Api]: ', err)
                return api.getCatchResponse(err)
            })
    }

    /**
     * Changes the network based on the given network ID.
     *
     * @param {number|string} network_id - The ID of the network to switch to.
     * @returns {Promise<Object>} A promise that resolves to the response data from the API.
     */
    changeNetwork(network_id) {
        const payload = { network_id }

        return api
            .sentRequest('/api/auth/change-network', payload)
            .then((res) => {
                return res.data
            })
            .catch((err) => {
                console.error('[Auth Api]: ', err)
                return api.getCatchResponse(err)
            })
    }

    /**
     * Uploads an avatar file to the server.
     *
     * @param {File} avatar - The avatar file to be uploaded.
     * @returns {Promise<Object>} The response data from the server.
     */
    uploadAvatar(avatar) {
        const formData = new FormData()
        formData.append('avatar', avatar)

        return api
            .sentFileUploadRequest('/api/auth/upload-avatar', formData)
            .then((res) => {
                return res.data
            })
            .catch((err) => {
                console.error('[Auth Api]: ', err)
                return api.getCatchResponse(err)
            })
    }

    /**
     * Uploads a new logo through an API request.
     *
     * @param {File} logo - The logo file to be uploaded.
     * @returns {Promise<Object>} The response data from the server.
     */
    uploadLogo(logo) {
        const formData = new FormData()
        formData.append('logo', logo)

        return api
            .sentFileUploadRequest('/api/auth/upload-logo', formData)
            .then((res) => {
                return res.data
            })
            .catch((err) => {
                console.error('[Auth Api]: ', err)
                return api.getCatchResponse(err)
            })
    }

    /**
     * Removes the current user's logo.
     *
     * @param {string} user_id - The unique identifier of the current user.
     * @returns {Promise<Object>} The response data from the server.
     */
    removeLogo(user_id) {
        const payload = { user_id }

        return api
            .sentRequest('/api/admin/user/remove-logo', payload)
            .then((res) => {
                return res.data
            })
            .catch((err) => {
                console.error('[Auth Api]: ', err)
                return api.getCatchResponse(err)
            })
    }

    /**
     * Updates the notification settings for the user.
     *
     * @param {string} type - The type of notification setting to update.
     * @param {boolean} value - The new value for the notification setting.
     * @returns {Promise<Object>} A promise that resolves to the response data from the API.
     */
    updateNotificationSetting(type, value) {
        const payload = { type, value }

        return api
            .sentRequest('/api/auth/update-notification-setting', payload)
            .then((res) => {
                return res.data
            })
            .catch((err) => {
                console.error('[Auth Api]: ', err)
                return api.getCatchResponse(err)
            })
    }

    /**
     * Updates the user's password by sending a request to the API.
     *
     * @param {string} old_password - The current password of the user.
     * @param {string} new_password - The new password to be set for the user.
     * @returns {Promise<Object>} A promise that resolves to the response data from the API or an error response.
     */
    updatePassword(old_password, new_password) {
        const payload = { old_password, new_password }

        return api
            .sentRequest('/api/auth/update-password', payload)
            .then((res) => {
                return res.data
            })
            .catch((err) => {
                console.error('[Auth Api]: ', err)
                return api.getCatchResponse(err)
            })
    }

    /**
     * Changes the visibility of the user menu.
     *
     * @param {boolean} usr_menu_visible - Indicates whether the user menu should be visible.
     * @returns {Promise<Object>} A promise that resolves to the response data or the error response.
     */
    changeMenuVisibility(usr_menu_visible) {
        const payload = { usr_menu_visible }

        return api
            .sentRequest('/api/auth/change-menu-visibility', payload)
            .then((res) => {
                return res.data
            })
            .catch((err) => {
                console.error('[Auth Api]: ', err)
                return api.getCatchResponse(err)
            })
    }
}

export const authApi = new AuthApi()
